import { Link } from 'react-router-dom'
import type { Client, ClientStatus } from '../../types/database.types'

interface ClientCardProps {
  client: Client & { assignment_count?: number }
  onEdit?: () => void
  onArchive?: () => void
  onResendInvitation?: () => void
  isResending?: boolean
}

// Status icons and colors for each status
const statusConfig: Record<ClientStatus, { bg: string; iconColor: string; label: string }> = {
  invited: { bg: 'bg-yellow-100', iconColor: 'text-yellow-600', label: 'Invited' },
  active: { bg: 'bg-green-100', iconColor: 'text-green-600', label: 'Active' },
  expired: { bg: 'bg-red-100', iconColor: 'text-red-600', label: 'Expired' },
  archived: { bg: 'bg-gray-100', iconColor: 'text-gray-500', label: 'Archived' },
}

// Tooltip wrapper component
function Tooltip({ children, label }: { children: React.ReactNode; label: string }) {
  return (
    <div className="relative group">
      {children}
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-[60]">
        {label}
        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
      </div>
    </div>
  )
}

// Status icon component
function StatusIcon({ status }: { status: ClientStatus }) {
  const config = statusConfig[status]

  if (status === 'active') {
    return (
      <svg className={`w-3.5 h-3.5 ${config.iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
      </svg>
    )
  }
  if (status === 'invited') {
    return (
      <svg className={`w-3.5 h-3.5 ${config.iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    )
  }
  if (status === 'expired') {
    return (
      <svg className={`w-3.5 h-3.5 ${config.iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    )
  }
  // archived
  return (
    <svg className={`w-3.5 h-3.5 ${config.iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
    </svg>
  )
}

export function ClientCard({
  client,
  onEdit,
  onArchive,
  onResendInvitation,
  isResending = false,
}: ClientCardProps) {
  const config = statusConfig[client.status]

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-md transition-shadow relative">
      {/* Status indicators - top right corner */}
      <div className="absolute top-4 right-4 flex items-center gap-1.5">
        {/* Account icon if client has login credentials */}
        {client.user_id && (
          <Tooltip label="Has login account">
            <span className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center cursor-default">
              <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </span>
          </Tooltip>
        )}
        {/* Status icon */}
        <Tooltip label={config.label}>
          <span className={`w-7 h-7 rounded-full ${config.bg} flex items-center justify-center cursor-default`}>
            <StatusIcon status={client.status} />
          </span>
        </Tooltip>
      </div>

      {/* Client info */}
      <div className="flex items-center gap-4 pr-20">
        <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
          <span className="text-lg font-medium text-gray-600">
            {client.full_name.charAt(0).toUpperCase()}
          </span>
        </div>
        <div className="min-w-0">
          <Link
            to={`/dashboard/clients/${client.id}`}
            className="font-semibold text-gray-900 hover:text-blue-600 block truncate"
          >
            {client.full_name}
          </Link>
          <p className="text-sm text-gray-500 truncate">{client.email}</p>
        </div>
      </div>

      {client.phone && (
        <p className="mt-4 text-sm text-gray-600">
          <span className="text-gray-500">Phone:</span> {client.phone}
        </p>
      )}

      <div className="mt-4 flex items-center justify-between">
        <div className="text-sm text-gray-500">
          {client.assignment_count !== undefined && (
            <span>{client.assignment_count} assignments</span>
          )}
        </div>
        <div className="flex gap-2">
          {/* Show send link button - text varies based on whether client has account */}
          {onResendInvitation && (
            <button
              onClick={onResendInvitation}
              disabled={isResending}
              className="text-sm text-green-600 hover:text-green-800 disabled:text-gray-400 disabled:cursor-not-allowed"
            >
              {isResending ? 'Sending...' : client.user_id ? 'Send Link' : 'Resend Invite'}
            </button>
          )}
          {onEdit && (
            <button
              onClick={onEdit}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Edit
            </button>
          )}
          {onArchive && client.status !== 'archived' && (
            <button
              onClick={onArchive}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Archive
            </button>
          )}
        </div>
      </div>

      {client.access_expires_at && (
        <p className="mt-2 text-xs text-gray-400">
          Access expires:{' '}
          {new Date(client.access_expires_at).toLocaleDateString()}
        </p>
      )}
    </div>
  )
}
